import { useAtom } from 'jotai';

import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { FilterParams } from '@core/Entities/Search/QueryParams.entity';
import type { Logos } from '@search-filters/Components/MakeLabel/Logos';
import MakeLabel from '@search-filters/Components/MakeLabel/MakeLabel';
import { useFilterLink } from '@search-filters/Hooks/UseFilterLink/UseFilterLink';
import { useSearchFilter } from '@search-filters/Hooks/UseSearchFilter';

import { FilterLink } from '../FilterLink/FilterLink';
import { POPULAR_MAKES } from './MakeFilter.constants';

const filterId: keyof FilterParams = 'make';

function useMakeFilter(
  isPlpFiltersSeoEnabled: boolean,
  tenant: Tenant | undefined,
  applyButton?: boolean,
) {
  const [{ makes }] = useAtom(aggregationsAtom);
  const { getLinkForFilterValue } = useFilterLink({
    filterId,
    isPlpFiltersSeoEnabled,
    tenant,
  });

  const allMakes = makes.map(make => {
    const maybeLink = getLinkForFilterValue({
      filterValue: make.key,
      resultCount: make.count ?? 0,
      isOnlyOption: makes.length === 1,
    });

    return {
      id: make.key,
      renderLabel: (
        <MakeLabel
          count={make.count}
          make={make.make as Logos}
          name={make.displayName}
          nameRender={<FilterLink link={maybeLink}>{make.displayName}</FilterLink>}
        />
      ),
      label: make.displayName,
    };
  });

  const { allOptions, ...rest } = useSearchFilter({
    filterId,
    data: allMakes,
    applyButton,
  });

  const popularMakes = allOptions.filter(make => POPULAR_MAKES.includes(make.id));

  return {
    popularMakes,
    allMakes: allOptions,
    ...rest,
  };
}

export default useMakeFilter;
